// The five windows the dashboard offers, and the date maths behind each.
//
// Luxon rather than hand-rolled offsets. JST has no daylight saving, so the
// arithmetic was *mostly* safe by hand — but month and quarter boundaries are
// exactly where hand-rolled date code goes wrong, and `startOf('quarter')` is
// not something worth reimplementing.
//
// Every window is [from, to) as a UTC instant; every label is JST, because rows
// are stored in UTC and read in Tokyo.

import { DateTime, FixedOffsetZone } from 'luxon'

/** Japan, by name: for Intl (`timeZone`). */
export const ZONE = 'Asia/Tokyo'

/**
 * The same zone as Luxon reads it: UTC+9, fixed. Japan has had no daylight
 * saving since 1951, so it is the same calendar as 'Asia/Tokyo', without
 * asking Intl for the offset of every date, which was most of the dashboard's
 * first-load script time (docs/DESIGN.md, "Responsiveness").
 */
export const JST = FixedOffsetZone.instance(9 * 60)
export const PERIODS = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly']

/** Luxon's unit for each period. Weeks start Monday, which is Luxon's default. */
const UNIT = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
  quarterly: 'quarter',
  yearly: 'year',
}

const at = (anchor) => DateTime.fromISO(anchor, { zone: JST })

/**
 * Labels come in two languages (decision 0030). English is the default, so
 * every caller that passes no language reads exactly as before; Japanese is
 * passed by the app (web/app/lib/periodLabels.js) and, through `?lang=ja`, by
 * the Worker. Dates in Japanese read 2026年10月13日（火）.
 */
const JA = 'ja'
const jaDay = (d) => d.toFormat("yyyy'年'M'月'd'日'")
const jaMonthDay = (d) => d.toFormat("M'月'd'日'")
const jaWeekday = (d) => d.setLocale('ja').toFormat('ccc')

export const jstDate = (iso) => DateTime.fromISO(iso, { zone: JST }).toISODate()
export const todayJst = () => DateTime.now().setZone(JST).toISODate()

/**
 * The window a period and anchor select.
 *
 * Calendar-aligned, not rolling: "September" is what a reader means by monthly,
 * and a rolling 30 days is not comparable between months.
 */
export function windowFor(period, anchor) {
  const unit = UNIT[period] ?? 'day'
  const start = at(anchor).startOf(unit)
  return { from: start.toUTC().toISO(), to: start.plus({ [unit]: 1 }).toUTC().toISO() }
}

/**
 * The canonical anchor for a period: the first day of the window a date falls in.
 *
 * Switching period keeps the reader where they were — daily 2026-09-18 becomes
 * the week that contains it, then the month that contains that. Snapping to
 * today instead would throw away the one thing they had already told us, and it
 * is the reason the period control and the date control have to share a model
 * rather than each holding their own. The browser and the Worker both call this
 * so a URL written by one is read the same way by the other.
 */
export function anchorFor(period, anchor) {
  // Pure, and asked for every stored day by the date picker and by "Latest",
  // each time the day list changes: worked out once per day and period.
  const key = `${period}|${anchor}`
  let found = anchors.get(key)
  if (found === undefined) {
    if (anchors.size > 20_000) anchors.clear()
    found = at(anchor).startOf(UNIT[period] ?? 'day').toISODate()
    anchors.set(key, found)
  }
  return found
}
const anchors = new Map()

/**
 * The JST date `n` days before an anchor.
 *
 * Used by the Trend page's "1 week ago / 1 month ago / 1 year ago" strip, which
 * asks for specific past days rather than a contiguous window.
 */
export function shiftDays(anchor, n) {
  return at(anchor).minus({ days: n }).toISODate()
}

/**
 * A span of `days` whole JST days ending with the anchor's own day.
 *
 * The Trend page's range selector works in days, not in calendar periods: a CPO
 * asking for "the last three months" wants ninety points, not three. The
 * existing `trendWindow` buckets by period and deliberately answers a different
 * question.
 */
export function dayRange(anchor, days, lang = 'en') {
  const end = at(anchor).startOf('day').plus({ days: 1 })
  const start = end.minus({ days })
  const last = end.minus({ days: 1 })
  return {
    from: start.toUTC().toISO(),
    to: end.toUTC().toISO(),
    unit: 'day',
    count: days,
    label:
      lang === JA
        ? `${jaDay(start)} – ${jaDay(last)}`
        : `${start.toFormat('d LLL yyyy')} – ${last.toFormat('d LLL yyyy')}`,
  }
}

/** The window immediately before this one, for the comparison column. */
export function previousWindow(period, anchor) {
  const unit = UNIT[period] ?? 'day'
  const start = at(anchor).startOf(unit).minus({ [unit]: 1 })
  return { from: start.toUTC().toISO(), to: start.plus({ [unit]: 1 }).toUTC().toISO() }
}

/** How the window reads in a header. */
export function windowLabel(period, anchor, lang = 'en') {
  const d = at(anchor)
  if (lang === JA) {
    if (period === 'daily') return `${jaDay(d)}（${jaWeekday(d)}）`
    if (period === 'weekly') {
      const start = d.startOf('week')
      return `${jaDay(start)}〜${jaMonthDay(start.endOf('week'))}`
    }
    if (period === 'monthly') return d.toFormat("yyyy'年'M'月'")
    if (period === 'quarterly') return `${d.year}年 第${d.quarter}四半期`
    return `${d.year}年`
  }
  if (period === 'daily') return d.toFormat('yyyy LLL dd (cccc)')
  if (period === 'weekly') {
    const start = d.startOf('week')
    return `${start.toFormat('yyyy LLL dd')} – ${start.endOf('week').toFormat('LLL dd')}`
  }
  if (period === 'monthly') return d.toFormat('yyyy LLLL')
  if (period === 'quarterly') return `${d.year} Q${d.quarter}`
  return String(d.year)
}

/** A short label, for a dropdown option. */
export function optionLabel(period, anchor, lang = 'en') {
  const d = at(anchor)
  if (period === 'daily') return d.toFormat('yyyy-LL-dd')
  if (lang === JA) {
    if (period === 'weekly') {
      const start = d.startOf('week')
      return `${start.weekYear}-W${String(start.weekNumber).padStart(2, '0')} · ${jaMonthDay(start)}〜${jaMonthDay(start.endOf('week'))}`
    }
    return windowLabel(period, anchor, JA)
  }
  if (period === 'weekly') {
    // ISO week number and the days it covers. The number alone is how a week is
    // named; the dates are how anyone checks they picked the right one.
    const start = d.startOf('week')
    return `${start.weekYear}-W${String(start.weekNumber).padStart(2, '0')} · ` +
      `${start.toFormat('LLL dd')} – ${start.endOf('week').toFormat('LLL dd')}`
  }
  if (period === 'monthly') return d.toFormat('yyyy LLLL')
  if (period === 'quarterly') return `${d.year} Q${d.quarter}`
  return String(d.year)
}

/**
 * The newest window of `period` that holds a day with a score: where "Latest"
 * goes. A day whose every check failed has rows and is still a window you can
 * open, but landing a reader on it by default showed "—" everywhere under a
 * fresh-looking date. Board DG2. `available` is /api/periods' body; with no
 * scored day at all, the newest window of any kind.
 */
export function latestWithScore(available, period) {
  const options = available?.options?.[period] ?? []
  const failed = new Set(available?.failedDays ?? [])
  // The window of the newest day with a score: one day's window worked out,
  // not every stored day's, which the dashboard paid for on every load.
  let newest = null
  for (const d of available?.days ?? []) if (!failed.has(d) && (!newest || d > newest)) newest = d
  const window = newest && anchorFor(period, newest)
  if (window && options.some((o) => o.value === window)) return window
  const scored = new Set(
    (available?.days ?? []).filter((d) => !failed.has(d)).map((d) => anchorFor(period, d)),
  )
  return (options.find((o) => scored.has(o.value)) ?? options[0])?.value ?? null
}

/**
 * The distinct periods covered by a list of JST dates, newest first.
 *
 * Built from the days that actually have data, so the picker cannot offer a
 * week nobody measured.
 */
export function periodsFrom(days, period, lang = 'en') {
  const unit = UNIT[period] ?? 'day'
  const seen = new Map()
  for (const day of days) {
    const start = DateTime.fromISO(day, { zone: JST }).startOf(unit)
    const key = start.toISODate()
    if (!seen.has(key)) seen.set(key, { value: key, label: optionLabel(period, key, lang) })
  }
  return [...seen.values()].sort((a, b) => b.value.localeCompare(a.value))
}

// ---------------------------------------------------------------- trends
//
// How far back a trend looks, per period. The point of a trend is "is this
// number typical", and that question needs enough history to answer and no
// more: 90 days under a quarterly view is the same window as the headline, so
// it says nothing, and 90 days under a daily view is three months of noise.

export const TREND_SPAN = { daily: 7, monthly: 3, quarterly: 2, yearly: 2 }

/** "7 days" in Japanese: the counter each unit takes. */
const JA_UNIT = { day: '日間', week: '週', month: 'か月', quarter: '四半期', year: '年' }

/** The bucket a row falls in, for a given period. One point per bucket. */
export function bucketKeyFor(period) {
  const unit = UNIT[period] ?? 'day'
  return (iso) => DateTime.fromISO(iso, { zone: JST }).startOf(unit).toISODate()
}

/** How a bucket reads on an axis — short, because there are a lot of them. */
export function bucketLabelFor(period, lang = 'en') {
  return (key) => {
    const d = at(key)
    if (lang === JA) {
      if (period === 'daily') return d.toFormat('M/d')
      if (period === 'weekly') return `W${String(d.weekNumber).padStart(2, '0')}`
      if (period === 'monthly') return d.toFormat("yy'年'M'月'")
      if (period === 'quarterly') return `${d.year} Q${d.quarter}`
      return `${d.year}年`
    }
    if (period === 'daily') return d.toFormat('LLL dd')
    if (period === 'weekly') return `W${String(d.weekNumber).padStart(2, '0')}`
    if (period === 'monthly') return d.toFormat('LLL yy')
    if (period === 'quarterly') return `${d.year} Q${d.quarter}`
    return String(d.year)
  }
}

/**
 * The window a trend covers: enough buckets to see a shape, and no more.
 *
 * A week of days, a month of weeks, a quarter of months, half a year of
 * quarters, two years. Few enough that every bucket gets a readable label and
 * the eye can hold the whole series at once — a thirty-point daily line under a
 * quarterly view is a texture, not a trend.
 *
 * Weekly is the odd one: its span is the anchor's calendar month rounded out to
 * whole weeks, so it shows the four, five or six weeks that month actually has
 * rather than a fixed count that straddles two months.
 */
export function trendWindow(period, anchor, lang = 'en') {
  const unit = UNIT[period] ?? 'day'

  if (period === 'weekly') {
    const month = at(anchor).startOf('month')
    const start = month.startOf('week')
    const end = month.endOf('month').startOf('week').plus({ week: 1 })
    const count = Math.round(end.diff(start, 'weeks').weeks)
    return {
      from: start.toUTC().toISO(),
      to: end.toUTC().toISO(),
      unit,
      count,
      label:
        lang === JA
          ? `${month.toFormat("yyyy'年'M'月'")}の${count}週`
          : `the ${count} weeks of ${month.toFormat('LLLL yyyy')}`,
    }
  }

  const count = TREND_SPAN[period] ?? 7
  const end = at(anchor).startOf(unit).plus({ [unit]: 1 })
  const start = end.minus({ [unit]: count })
  return {
    from: start.toUTC().toISO(),
    to: end.toUTC().toISO(),
    unit,
    count,
    label:
      lang === JA
        ? `${windowLabel(period, anchor, JA)}までの${count}${JA_UNIT[unit] ?? unit}`
        : `${count} ${unit}s to ${windowLabel(period, anchor)}`,
  }
}

/**
 * Every bucket in a window, in order, whether or not anything was measured.
 *
 * Derived from the window rather than from the rows. A bucket built only from
 * rows disappears when nothing was measured, so an axis that should show the
 * five weeks of September shows three and looks complete — while a week whose
 * measurements all failed correctly shows as a gap. Two kinds of missing, drawn
 * differently, is the bug GUARDRAILS §5 exists to prevent.
 */
export function bucketsIn(period, span) {
  const unit = UNIT[period] ?? 'day'
  const end = DateTime.fromISO(span.to, { zone: JST })
  const out = []
  let cursor = DateTime.fromISO(span.from, { zone: JST }).startOf(unit)
  while (cursor < end) {
    out.push(cursor.toISODate())
    cursor = cursor.plus({ [unit]: 1 })
  }
  return out
}

/**
 * The JST date of the deep-run day (`deep_run_day` of each month) inside a
 * window, the latest if there are several; null when there is none or the
 * deep run is off. docs/decisions/0014, 0023.
 */
export function deepDayIn({ from, to }, dayOfMonth) {
  if (!(dayOfMonth > 0)) return null
  let found = null
  // Each month the window touches: from its first JST day to its last.
  let month = jstDate(from).slice(0, 7)
  const last = jstDate(new Date(Date.parse(to) - 1).toISOString()).slice(0, 7)
  for (let i = 0; i < 25 && month <= last; i += 1) {
    const day = `${month}-${String(dayOfMonth).padStart(2, '0')}`
    const start = windowFor('daily', day).from
    if (start >= from && start < to) found = day
    const [y, m] = month.split('-').map(Number)
    month = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`
  }
  return found
}

/**
 * The reader's own day, placed in the window on screen.
 *
 * `focus` is the last day the reader chose (useWindow). Switching period snaps
 * the date to the new window's start, so daily 6 Oct → weekly → daily used to
 * land on Monday 5 Oct, and via yearly on 1 Jan. The day is kept apart from the
 * anchor so the way back finds it: inside this window it is the day itself;
 * in another window of the same period it is the same place in that window
 * (6 Oct seen from September is 6 Sep, clamped to the month's end); with no
 * day chosen it is the window's start, as before.
 */
export function dayInWindow(period, anchor, focus) {
  if (!focus || !anchor) return anchor
  if (anchorFor(period, focus) === anchor) return focus
  const unit = `${UNIT[period] ?? 'day'}s`
  const steps = Math.round(at(anchor).diff(at(anchorFor(period, focus)), unit).as(unit))
  const moved = at(focus).plus({ [unit]: steps }).toISODate()
  return anchorFor(period, moved) === anchor ? moved : anchor
}
